import DatePicker from "react-datepicker";
import { LuCalendar } from "react-icons/lu";
import { calculateInitialOpenDate } from "../../../utils/planManager/planConfigurationUtils.js";

import "./DateField.scss";

/**
 * Parses an MM-DD-YYYY string into a local Date, or null when empty/invalid.
 * @param {string} value
 * @returns {Date | null}
 */
const parseMmDdYyyyToDate = (value) => {
    if (value == null || value === "") {
        return null;
    }

    const parts = value.split("-");
    if (parts.length !== 3) {
        return null;
    }

    const month = Number(parts[0]);
    const day = Number(parts[1]);
    const year = Number(parts[2]);

    if (
        !Number.isInteger(month) ||
        !Number.isInteger(day) ||
        !Number.isInteger(year) ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31 ||
        year < 1000 ||
        year > 9999
    ) {
        return null;
    }

    const parsedDate = new Date(year, month - 1, day);
    if (
        parsedDate.getFullYear() !== year ||
        parsedDate.getMonth() !== month - 1 ||
        parsedDate.getDate() !== day
    ) {
        return null;
    }

    return parsedDate;
};

/**
 * Formats a Date as an MM-DD-YYYY string, or empty string when invalid.
 * @param {Date | null} date
 * @returns {string}
 */
const formatDateToMmDdYyyy = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return "";
    }

    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear());

    return `${month}-${day}-${year}`;
};

/**
 * Blocks printable keyboard input while preserving navigation keys.
 * @param {React.KeyboardEvent<HTMLElement>} event
 */
const preventManualDateEntryKeyDown = (event) => {
    if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
    }

    if (event.key.length === 1) {
        event.preventDefault();
    }
};

/**
 * Controlled labeled date input for Plan Manager forms. Pure presentational —
 * emits MM-DD-YYYY strings through callbacks; owns no state. Dates are selected
 * via the calendar; manual keyboard entry is blocked by input event handlers.
 *
 * @param {object} props
 * @param {string} props.id - Input id; links label via htmlFor.
 * @param {string} props.label - Visible label text.
 * @param {string} props.value - Controlled value in MM-DD-YYYY format.
 * @param {(value: string) => void} props.onChange - Emits the new MM-DD-YYYY string (empty when cleared).
 * @param {string} [props.placeholder] - Input placeholder.
 * @param {string} [props.helperText] - Optional hint below the input.
 * @param {string} [props.error] - Optional error message with role="alert".
 * @param {boolean} [props.disabled] - Disables the input.
 * @param {string} [props.className] - Extra class on the root element.
 * @param {string} [props.min] - Optional minimum date in MM-DD-YYYY format.
 * @param {string} [props.max] - Optional maximum date in MM-DD-YYYY format.
 * @param {string} [props.autoComplete="off"] - Autocomplete behavior for the input element.
 * @returns {import('react').JSX.Element}
 */
const DateField = ({
    id,
    label,
    value,
    onChange,
    placeholder,
    helperText,
    error,
    disabled,
    className,
    min,
    max,
    autoComplete = "off",
}) => {
    const rootClassName = [
        "form-input",
        "plan-manager-date-field",
        error ? "plan-manager-date-field--error" : null,
        className,
    ]
        .filter(Boolean)
        .join(" ");

    const selectedDate = parseMmDdYyyyToDate(value);
    const minDate = min ? parseMmDdYyyyToDate(min) : undefined;
    const maxDate = max ? parseMmDdYyyyToDate(max) : undefined;
    const openToDate = calculateInitialOpenDate(selectedDate, minDate, maxDate);

    return (
        <div className={rootClassName}>
            <label htmlFor={id}>{label}</label>
            <div className="plan-manager-date-field__control">
                <LuCalendar
                    className="plan-manager-date-field__icon"
                    aria-hidden
                    size={16}
                />
                <DatePicker
                    id={id}
                    selected={selectedDate}
                    openToDate={openToDate}
                    onChange={(date) =>
                        onChange(date ? formatDateToMmDdYyyy(date) : "")
                    }
                    dateFormat="M/d/yyyy"
                    placeholderText={placeholder ?? "MM/DD/YYYY"}
                    onKeyDown={preventManualDateEntryKeyDown}
                    onChangeRaw={(event) => event?.preventDefault()}
                    isClearable
                    disabled={disabled}
                    minDate={minDate ?? undefined}
                    maxDate={maxDate ?? undefined}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    className="date-picker"
                    wrapperClassName="date-picker-wrapper"
                    popperPlacement="bottom-start"
                    autoComplete={autoComplete}
                />
            </div>
            {helperText ? (
                <p className="plan-manager-date-field__helper">{helperText}</p>
            ) : null}
            {error ? (
                <p className="plan-manager-date-field__error" role="alert">
                    {error}
                </p>
            ) : null}
        </div>
    );
};

export default DateField;
